import React, { useState, useEffect } from 'react';
import {
  BookOpen, Plus, Search, Filter, Calendar, User, Tag,
  Edit2, Trash2, Eye, X, Save, Clock,
  ChevronDown, ChevronUp, FileText, Download
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { formatDate, hasPermission } from '../../utils/helpers';
import jsPDF from 'jspdf';
import { logger, performance, logFirebaseError } from '../../utils/logger';

import 'jspdf-autotable';

const ChurchJournal = () => {
  const { currentUser } = useAuth();
  const { data, addJournalEntry, updateJournalEntry, deleteJournalEntry } = useData();

  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDate, setFilterDate] = useState('all');
  const [expandedEntries, setExpandedEntries] = useState([]);

  // Catégories du journal
  const categories = [
    { id: 'culte', label: 'Culte & Prière', icon: '🙏', color: 'blue' },
    { id: 'event', label: 'Événement', icon: '🎉', color: 'purple' },
    { id: 'decision', label: 'Décision', icon: '⚖️', color: 'green' },
    { id: 'visite', label: 'Visite Pastorale', icon: '🏠', color: 'orange' },
    { id: 'anniversaire', label: 'Anniversaire', icon: '🎂', color: 'pink' },
    { id: 'finance', label: 'Finance', icon: '💰', color: 'emerald' },
    { id: 'membre', label: 'Membre', icon: '👤', color: 'indigo' },
    { id: 'autre', label: 'Autre', icon: '📝', color: 'gray' }
  ];

  // Formulaire
  const [formData, setFormData] = useState({
    title: '',
    category: 'culte',
    date: new Date().toISOString().split('T')[0],
    content: '',
    participants: '',
    tags: '',
    importance: 'normal',
    attachments: []
  });

  // Charger les entrées
  useEffect(() => {
    const journalEntries = data.churchJournal || [];
    setEntries(journalEntries);
    setFilteredEntries(journalEntries);
  }, [data.churchJournal]);

  // Filtrer les entrées
  useEffect(() => {
    let filtered = [...entries];

    if (searchTerm) {
      filtered = filtered.filter(entry =>
        entry.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.tags?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterCategory !== 'all') {
      filtered = filtered.filter(entry => entry.category === filterCategory);
    }

    if (filterDate !== 'all') {
      const now = new Date();
      filtered = filtered.filter(entry => {
        const entryDate = new Date(entry.date);
        switch (filterDate) {
          case 'today':
            return entryDate.toDateString() === now.toDateString();
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return entryDate >= weekAgo;
          case 'month':
            return entryDate.getMonth() === now.getMonth() && 
                   entryDate.getFullYear() === now.getFullYear();
          case 'year':
            return entryDate.getFullYear() === now.getFullYear();
          default:
            return true;
        }
      });
    }

    filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
    setFilteredEntries(filtered);
  }, [searchTerm, filterCategory, filterDate, entries]);

  // ========== EXPORT PDF PROFESSIONNEL ==========
  
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;

    // Fonction pour ajouter une nouvelle page si nécessaire
    const checkPageBreak = (requiredHeight) => {
      if (yPos + requiredHeight > pageHeight - margin) {
        doc.addPage();
        yPos = margin;
        return true;
      }
      return false;
    };

    // En-tête du document
    doc.setFillColor(37, 99, 235); // Bleu
    doc.rect(0, 0, pageWidth, 35, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Journal de l\'Église', pageWidth / 2, 15, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('VOMM FJKM Hannover', pageWidth / 2, 25, { align: 'center' });

    yPos = 45;

    // Informations du rapport
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Date d'export: ${new Date().toLocaleDateString('fr-FR')}`, margin, yPos);
    doc.text(`Exporté par: ${currentUser.name}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 7;

    // Statistiques
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Statistiques', margin, yPos);
    yPos += 7;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total entrées: ${filteredEntries.length}`, margin, yPos);
    doc.text(`Période: ${getFilterDateLabel()}`, margin + 70, yPos);
    
    if (filterCategory !== 'all') {
      const cat = getCategory(filterCategory);
      doc.text(`Catégorie: ${cat.label}`, margin + 140, yPos);
    }
    
    yPos += 10;
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;

    // Entrées du journal
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Entrées du Journal', margin, yPos);
    yPos += 10;

    filteredEntries.forEach((entry, index) => {
      const category = getCategory(entry.category);
      
      // Vérifier si on a assez de place pour l'entrée (minimum 50mm)
      checkPageBreak(50);

      // Cadre de l'entrée
      const entryStartY = yPos;
      
      // En-tête de l'entrée avec fond coloré
      doc.setFillColor(240, 240, 245);
      doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 10, 2, 2, 'F');
      
      // Numéro et titre
      doc.setTextColor(37, 99, 235);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`${index + 1}. ${entry.title}`, margin + 3, yPos + 7);
      
      // Badge importance si haute
      if (entry.importance === 'high') {
        doc.setFillColor(239, 68, 68);
        doc.roundedRect(pageWidth - margin - 30, yPos + 2, 25, 6, 1, 1, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.text('IMPORTANT', pageWidth - margin - 27, yPos + 6);
      }
      
      yPos += 12;

      // Métadonnées
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      
      const metadata = [
        `[${category.label}]`,
        `Date: ${formatDate(entry.date)}`,
        `Auteur: ${entry.author}`
      ].join('  |  ');
      
      doc.text(metadata, margin + 3, yPos);
      yPos += 7;

      // Contenu
      doc.setTextColor(50, 50, 50);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      const contentLines = doc.splitTextToSize(entry.content, pageWidth - 2 * margin - 6);
      contentLines.forEach((line, idx) => {
        if (idx > 0) checkPageBreak(5);
        doc.text(line, margin + 3, yPos);
        yPos += 5;
      });

      yPos += 2;

      // Participants
      if (entry.participants) {
        checkPageBreak(7);
        doc.setTextColor(70, 70, 70);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('Participants: ', margin + 3, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(entry.participants, margin + 25, yPos);
        yPos += 6;
      }

      // Tags
      if (entry.tags) {
        checkPageBreak(7);
        doc.setTextColor(37, 99, 235);
        doc.setFontSize(8);
        const tags = entry.tags.split(',').map(t => `#${t.trim()}`).join(' ');
        doc.text(tags, margin + 3, yPos);
        yPos += 6;
      }

      // Ligne de séparation
      yPos += 3;
      doc.setDrawColor(220, 220, 220);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;
    });

    // Pied de page sur toutes les pages
    const totalPages = doc.internal.pages.length - 1; // -1 car pages commence à 1
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(8);
      doc.text(
        `Page ${i} sur ${totalPages}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
      doc.text(
        'VOMM FJKM Hannover - Journal de l\'Église',
        margin,
        pageHeight - 10
      );
      doc.text(
        new Date().toLocaleDateString('fr-FR'),
        pageWidth - margin,
        pageHeight - 10,
        { align: 'right' }
      );
    }

    // Générer le nom du fichier
    const filename = `Journal_Eglise_${new Date().toISOString().split('T')[0]}.pdf`;
    
    // Télécharger
    doc.save(filename);
    
    logger.log('✅ PDF exporté:', filename);
  };

  // Fonction helper pour obtenir le label du filtre de date
  const getFilterDateLabel = () => {
    switch (filterDate) {
      case 'today': return 'Aujourd\'hui';
      case 'week': return 'Cette semaine';
      case 'month': return 'Ce mois';
      case 'year': return 'Cette année';
      default: return 'Toutes les dates';
    }
  };

  // Ajouter ou modifier une entrée
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (selectedEntry) {
        // Mode édition
        await updateJournalEntry(selectedEntry.id, formData);
      } else {
        // Mode ajout
        const newEntry = {
          ...formData,
          author: currentUser.name,
          authorId: currentUser.id
        };
        await addJournalEntry(newEntry);
      }

      setFormData({
        title: '',
        category: 'culte',
        date: new Date().toISOString().split('T')[0],
        content: '',
        participants: '',
        tags: '',
        importance: 'normal',
        attachments: []
      });

      setSelectedEntry(null);
      setShowAddModal(false);
    } catch (error) {
      logger.error('Erreur sauvegarde entrée:', error);
      alert('Erreur lors de la sauvegarde de l\'entrée');
    }
  };

  // Supprimer une entrée
  const handleDelete = async (entryId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette entrée ?')) {
      try {
        await deleteJournalEntry(entryId);
      } catch (error) {
        logger.error('Erreur suppression entrée:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  // Modifier une entrée
  const handleEdit = (entry) => {
    setFormData({
      title: entry.title || '',
      category: entry.category || 'culte',
      date: entry.date || new Date().toISOString().split('T')[0],
      content: entry.content || '',
      participants: entry.participants || '',
      tags: entry.tags || '',
      importance: entry.importance || 'normal',
      attachments: entry.attachments || []
    });
    setSelectedEntry(entry);
    setShowAddModal(true);
  };

  const toggleExpand = (entryId) => {
    setExpandedEntries(prev =>
      prev.includes(entryId)
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const getCategory = (categoryId) => {
    return categories.find(c => c.id === categoryId) || categories[categories.length - 1];
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 'high': return 'text-red-600 dark:text-red-400';
      case 'normal': return 'text-blue-600 dark:text-blue-400';
      case 'low': return 'text-gray-600 dark:text-gray-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white flex items-center gap-3">
              <BookOpen size={32} className="text-blue-600" />
              Journal de l'Église
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Historique et événements de la communauté
            </p>
          </div>

          <div className="flex gap-2">
            {/* Bouton Export PDF */}
            <button
              onClick={exportToPDF}
              disabled={filteredEntries.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              title="Exporter en PDF"
            >
              <Download size={20} />
              Export PDF
            </button>

            {hasPermission(currentUser, 'manage_programmes') && (
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
              >
                <Plus size={20} />
                Nouvelle Entrée
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              id="journal-search"
              name="search"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <select
              id="filter-category"
              name="filterCategory"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Toutes les catégories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <select
              id="filter-date"
              name="filterDate"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Toutes les dates</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="year">Cette année</option>
            </select>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Entrées</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">{entries.length}</p>
            </div>
            <BookOpen className="text-blue-600" size={32} />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Ce Mois</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">
                {entries.filter(e => {
                  const d = new Date(e.date);
                  const now = new Date();
                  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                }).length}
              </p>
            </div>
            <Calendar className="text-green-600" size={32} />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Catégories</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">{categories.length}</p>
            </div>
            <Tag className="text-purple-600" size={32} />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Résultats</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">{filteredEntries.length}</p>
            </div>
            <Filter className="text-orange-600" size={32} />
          </div>
        </div>
      </div>

      {/* Liste des entrées */}
      <div className="space-y-4">
        {filteredEntries.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
            <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm || filterCategory !== 'all' || filterDate !== 'all'
                ? 'Aucune entrée ne correspond aux filtres'
                : 'Aucune entrée dans le journal'}
            </p>
          </div>
        ) : (
          filteredEntries.map(entry => {
            const category = getCategory(entry.category);
            const isExpanded = expandedEntries.includes(entry.id);

            return (
              <div
                key={entry.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl">{category.icon}</span>
                        <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                          {entry.title}
                        </h3>
                        {entry.importance === 'high' && (
                          <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-semibold rounded">
                            Important
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {formatDate(entry.date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <User size={14} />
                          {entry.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {new Date(entry.createdAt?.toDate ? entry.createdAt.toDate() : entry.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${category.color}-100 dark:bg-${category.color}-900/30 text-${category.color}-600 dark:text-${category.color}-400`}>
                          {category.label}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleExpand(entry.id)}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title={isExpanded ? 'Réduire' : 'Développer'}
                      >
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>

                      <button
                        onClick={() => {
                          setSelectedEntry(entry);
                          setShowViewModal(true);
                        }}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-blue-600"
                        title="Voir détails"
                      >
                        <Eye size={20} />
                      </button>

                      {hasPermission(currentUser, 'manage_programmes') && (
                        <>
                          <button
                            onClick={() => handleEdit(entry)}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-green-600"
                            title="Modifier"
                          >
                            <Edit2 size={20} />
                          </button>

                          <button
                            onClick={() => handleDelete(entry.id)}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-red-600"
                            title="Supprimer"
                          >
                            <Trash2 size={20} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className={`text-gray-700 dark:text-gray-300 ${isExpanded ? '' : 'line-clamp-2'}`}>
                    {entry.content}
                  </div>

                  {entry.tags && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {entry.tags.split(',').map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded"
                        >
                          #{tag.trim()}
                        </span>
                      ))}
                    </div>
                  )}

                  {isExpanded && entry.participants && (
                    <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Participants:
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {entry.participants}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal Ajout */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                Nouvelle Entrée
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label htmlFor="journal-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Titre *
                </label>
                <input
                  type="text"
                  id="journal-title"
                  name="title"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Culte de rentrée 2024"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="journal-category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Catégorie *
                  </label>
                  <select
                    id="journal-category"
                    name="category"
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500"
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.icon} {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="journal-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    id="journal-date"
                    name="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="journal-importance" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Importance
                </label>
                <select
                  id="journal-importance"
                  name="importance"
                  value={formData.importance}
                  onChange={(e) => setFormData({ ...formData, importance: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Faible</option>
                  <option value="normal">Normale</option>
                  <option value="high">Haute</option>
                </select>
              </div>

              <div>
                <label htmlFor="journal-content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description *
                </label>
                <textarea
                  id="journal-content"
                  name="content"
                  required
                  rows={6}
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="Décrivez l'événement, les décisions prises, etc."
                />
              </div>

              <div>
                <label htmlFor="journal-participants" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Participants
                </label>
                <input
                  type="text"
                  id="journal-participants"
                  name="participants"
                  value={formData.participants}
                  onChange={(e) => setFormData({ ...formData, participants: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Pasteur, Président, 25 membres"
                />
              </div>

              <div>
                <label htmlFor="journal-tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tags (séparés par des virgules)
                </label>
                <input
                  type="text"
                  id="journal-tags"
                  name="tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: culte, rentrée, jeunesse"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Save size={20} />
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Vue Détails */}
      {showViewModal && selectedEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                {getCategory(selectedEntry.category).icon}
                {selectedEntry.title}
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Catégorie</p>
                  <p className="font-semibold text-gray-800 dark:text-white">
                    {getCategory(selectedEntry.category).label}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Date</p>
                  <p className="font-semibold text-gray-800 dark:text-white">
                    {formatDate(selectedEntry.date)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Auteur</p>
                  <p className="font-semibold text-gray-800 dark:text-white">
                    {selectedEntry.author}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Importance</p>
                  <p className={`font-semibold ${getImportanceColor(selectedEntry.importance)}`}>
                    {selectedEntry.importance === 'high' ? 'Haute' : selectedEntry.importance === 'normal' ? 'Normale' : 'Faible'}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                  Description
                </h3>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {selectedEntry.content}
                </p>
              </div>

              {selectedEntry.participants && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                    Participants
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    {selectedEntry.participants}
                  </p>
                </div>
              )}

              {selectedEntry.tags && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedEntry.tags.split(',').map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-sm"
                      >
                        #{tag.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChurchJournal;